import {
  AccountInfo, AuthenticationResult, InteractionRequiredAuthError, PublicClientApplication, RedirectRequest, SilentRequest,
} from '@azure/msal-browser';
import { authConfig } from 'config/authConfig';
import { parsePolicyId, resolvePolicy } from 'helpers/auth/resolvePolicy';
import { hasRedirectResponse, resolveAuthStatus } from 'helpers/auth/resolveAuthStatus';
import log from 'helpers/log';
import { LogLevel } from 'types/log';
import { AuthStatus, PolicyClaims, ResolvedPolicy } from 'types/auth';

let pca: PublicClientApplication;

const URL_PARAMS_KEY = 'urlParams';
// Set immediately before an automatic redirect so a sign-in that comes back without an
// account leaves the user on a sign-in screen instead of bouncing to B2C forever.
const SSO_ATTEMPTED_KEY = 'ssoAttempted';

// Starts as `resolving` because the redirect is consumed before the first render, and stays there
// while a redirect is pending. Only `signedOut` means a sign-in finished without an account.
let authStatus: AuthStatus = 'resolving';

// DEV SHORTCUT: To test locally without needing to set up the login process, set the desktop application variable to true.
// This will bypass the login, and directly load into the sketchpad. It will have no access to the cloud services, however.
let desktopApplication = false;

// The desktop Application is launched through chromium.
// If we detect Report32.exe as a userAgent, we are in the desktop Application.
// The wrapper still appends `application` and `nva` launch parameters; both are ignored.
export const isDesktopApplication = (): boolean => {
  if (desktopApplication) return true;
  // the chromium control will have this userAgent
  desktopApplication = Boolean(navigator.userAgent.match(/Report32.exe/));
  return desktopApplication;
};

// STP SSS-420
// The MobileApp attaches the ReactNativeWebView to the window.
// If the ReactNativeWebView is present, then this is the mobile app.
let mobileApplication = false;
export const isMobileApplication = (): boolean => {
  if (mobileApplication) return true;
  // set the boolean so we don't re-evaluate this over and over
  mobileApplication = Boolean(window.ReactNativeWebView);
  return mobileApplication;
};

// The Workbench Application will provide a URL Parameter indicate the SketchMode.
// When this URL Parameter is present and matches "workbench", we are in the workbench.
// * In the future, perhaps we should check for the Iframe instead.
let workbenchApplication = false;
export const isWorkbenchApplication = (): boolean => {
  if (workbenchApplication) return true;

  let sketchModeId;
  const storedParams = sessionStorage.getItem(URL_PARAMS_KEY);
  if (storedParams) {
    const queryParams = new URLSearchParams(storedParams);
    sketchModeId = queryParams.get('SketchMode');
  }

  if (sketchModeId && sketchModeId === 'workbench') {
    workbenchApplication = true;
  }
  return workbenchApplication;
};

// getSketchPadMode will check which mode the application is in, and return an enum
// that can be used instead of calling the checking functions directly.
export const enum SketchpadModeType {
  Default = 'default',
  Desktop = 'desktop',
  Mobile = 'mobile',
  Workbench = 'workbench',
  Unchecked = '',
}
let sketchPadMode = SketchpadModeType.Unchecked;
export const getSketchPadMode = (): SketchpadModeType => {
  if (sketchPadMode !== SketchpadModeType.Unchecked) return sketchPadMode;

  // NOTE: All modes are exclusive. We can only be in a single mode at a time.
  if (isDesktopApplication()) {
    sketchPadMode = SketchpadModeType.Desktop;
  } else if (isMobileApplication()) {
    sketchPadMode = SketchpadModeType.Mobile;
  } else if (isWorkbenchApplication()) {
    sketchPadMode = SketchpadModeType.Workbench;
  } else {
    sketchPadMode = SketchpadModeType.Default;
  }

  sessionStorage.setItem('sketchMode', sketchPadMode.toString());
  return sketchPadMode;
};

// Desktop runs entirely offline and never signs in; every other mode needs an account.
const MODES_REQUIRING_LOGIN = [SketchpadModeType.Default, SketchpadModeType.Mobile, SketchpadModeType.Workbench];

export const requiresLogin = (): boolean => MODES_REQUIRING_LOGIN.indexOf(getSketchPadMode()) !== -1;

// The launch parameters are lost from the address bar by the authentication redirect,
// so they are captured before anything can navigate away.
export const captureUrlParams = (): void => {
  const urlParameters = new URLSearchParams(window.location.search).toString();
  if (urlParameters) {
    sessionStorage.setItem(URL_PARAMS_KEY, urlParameters);
  }
};

// The address bar wins while it still has the launch parameters; the stored copy covers
// every load after the redirect has stripped them.
const getRequestedPolicyId = (): string | undefined =>
  parsePolicyId(window.location.search) || parsePolicyId(sessionStorage.getItem(URL_PARAMS_KEY) || '');

const getAccount = (): AccountInfo | undefined => {
  const active = pca.getActiveAccount();
  if (active) return active;
  const [first] = pca.getAllAccounts();
  return first;
};

const getClaims = (account?: AccountInfo): PolicyClaims | undefined => account && account.idTokenClaims as PolicyClaims;

// Which policy a new sign-in should target. The partner link decides, and the default sign-in
// policy is used when the link says nothing or names a policy that is not allowed.
const requestedPolicy = (): ResolvedPolicy => resolvePolicy(authConfig.identity, getRequestedPolicyId());

// Which policy an existing account already belongs to. Token renewal and sign-out have to stay on
// the authority that issued the account, whatever the current link happens to ask for.
const accountPolicy = (account?: AccountInfo): ResolvedPolicy =>
  resolvePolicy(authConfig.identity, undefined, getClaims(account));

// utility functions
export const configureAuth = (): void => {
  const { authority } = requestedPolicy();
  pca = new PublicClientApplication({
    ...authConfig.msalConfig,
    auth: { ...authConfig.msalConfig.auth, authority },
  });
};

/*
Consume the authentication redirect before anything else reads the account cache.
MSAL only writes the returning account to its cache when this runs, so skipping it makes a
signed-in user look signed out and triggers a second trip to the identity provider.
*/
export const initializeAuth = async (): Promise<AccountInfo | undefined> => {
  captureUrlParams();

  // MSAL clears the response fragment as it processes it, so this has to be sampled first.
  const responsePresent = hasRedirectResponse(window.location.hash);

  /*
  Reading the account cache has to stay inside the guard as well. A failure here still has to reach
  a decided status, otherwise the sketch waits on a sign-in that nothing is going to finish.
  */
  let account: AccountInfo | undefined;
  try {
    const result: AuthenticationResult | null = await pca.handleRedirectPromise();
    account = (result && result.account) || getAccount();
    if (account) {
      pca.setActiveAccount(account);
      sessionStorage.removeItem(SSO_ATTEMPTED_KEY);
    }
  } catch (err) {
    log(LogLevel.error, 'initializeAuth - unable to process the authentication redirect', err);
  }

  /*
  An automatic sign-in that has not run yet is still owed, so this load is unresolved rather than
  signed out. Without that, the first paint of a partner handoff is a Sign In button that the
  pending redirect then replaces.
  */
  authStatus = resolveAuthStatus({
    hasAccount: Boolean(account),
    hasRedirectResponse: responsePresent,
    signInIsOwed: requiresLogin() && !hasAttemptedSso(),
  });

  if (authConfig.debugEnabled) {
    log(LogLevel.debug, 'initializeAuth', { signedIn: Boolean(account), policy: accountPolicy(account).policyId });
  }

  return account;
};

export const getUserIsValid = async (): Promise<boolean> => {
  const valid = Boolean(getAccount());

  if (authConfig.debugEnabled) {
    log(LogLevel.debug, 'getUserIsValid', valid);
  }

  return valid;
};

// True once an automatic sign-in has been started but has not produced an account.
export const hasAttemptedSso = (): boolean => sessionStorage.getItem(SSO_ATTEMPTED_KEY) === 'true';

export const getAuthStatus = (): AuthStatus => authStatus;

export const getAccessToken = async (): Promise<string | undefined> => {
  const account = getAccount();
  if (!account) return undefined;

  const request: SilentRequest = {
    scopes: [authConfig.eagleScope],
    account,
    authority: accountPolicy(account).authority,
  };

  try {
    const response = await pca.acquireTokenSilent(request);
    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      // The refresh needs the user, so hand it back to the identity provider rather than
      // letting callers fire requests with no token.
      await pca.acquireTokenRedirect({ scopes: request.scopes, account, authority: request.authority });
      return undefined;
    }
    log(LogLevel.error, 'getAccessToken - unable to acquire an access token', error);
    return undefined;
  }
};

const getUserInfo = (): PolicyClaims | undefined => getClaims(getAccount());

export const getEmail = async (): Promise<string | undefined> => {
  const userInfo = getUserInfo();
  const email = userInfo && userInfo.email;
  return typeof email === 'string' ? email : undefined;
};

/*
Send the browser to the identity provider for the policy this session belongs to.

This is a top level navigation, so an existing B2C session cookie for that policy is
honoured and the user comes straight back without seeing a prompt. Targeting the wrong
policy is what makes a partner hand-off ask for credentials a second time.
*/
export const login = async (policy?: Partial<RedirectRequest>): Promise<void> => {
  const { authority, domainHint } = requestedPolicy();

  // A caller that names its own authority, such as the password reset and sign up routes, takes
  // over completely so the resolved policy's hint does not follow it into a different flow.
  const resolved = policy && policy.authority
    ? {}
    : { authority, ...(domainHint ? { domainHint } : {}) };

  const request: RedirectRequest = {
    scopes: [authConfig.eagleScope],
    ...resolved,
    ...policy,
  };

  sessionStorage.setItem(SSO_ATTEMPTED_KEY, 'true');
  authStatus = 'resolving';
  try {
    await pca.loginRedirect(request);
  } catch (error) {
    // The navigation never happened, so this session really is signed out.
    authStatus = 'signedOut';
    throw error;
  }
};

// This function cleans up session/local storage before Logout or Exit
export const beforeLogout = async (): Promise<void> => {
  await sessionStorage.removeItem('userCompany');
  await sessionStorage.removeItem(URL_PARAMS_KEY);
  await sessionStorage.removeItem(SSO_ATTEMPTED_KEY);
  await localStorage.clear();
};

export const doSketchExit = async (orderId: string): Promise<void> => {
  if (isWorkbenchApplication()) {
    // EDGE CASE: Return to Workbench
    await returnToWorkbench(beforeLogout, orderId);
  } else {
    // DEFAULT CASE: Logout
    await logout(beforeLogout);
  }
};

// Logout, redirecting to the login screen (STANDALONE ONLY)
export const logout = async (preLogout?: () => void | Promise<void>): Promise<void> => {
  const account = getAccount();

  if (preLogout) {
    await preLogout();
  }

  await pca.logoutRedirect({
    account,
    authority: accountPolicy(account).authority,
  });
};

// Return to the workbench order (WORKBENCH ONLY)
export const returnToWorkbench = async (preReturn: (() => void | Promise<void>) | undefined, orderId: string): Promise<void> => {
  if (preReturn) {
    await preReturn();
  }

  const env = 'uat';
  const domain = `https://workbench.aci${env}.io`;
  const encodedOrderId = encodeURIComponent(orderId);

  window.location.href = `${domain}/reportbuilder/sketch/${encodedOrderId}`;
};
